"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connectWithTikTok, connectWithTikTokBusiness } from "@/app/api/social-connect";
import { useTranslation } from "react-i18next";
import { useTikTokIndex } from "./useTikTokIndex";
import { sessionKeys } from "@/lib/sessionKeys";

export interface TikTokAccount {
  id: string;
  username?: string;
  name?: string;
  profile_picture_url?: string;
  connectionId: string;
  connectionType: "tiktok" | "tiktok_business";
}

export interface UseTikTokConnectionsReturn {
  isLoading: boolean;
  isConnecting: boolean;
  accounts: TikTokAccount[];
  error: string | null;
  indexingMessages: string[];
  isIndexing: Record<string, boolean>;
  currentMessageIndex: Record<string, number>;
  triggerIndex: (accountId: string, connectionId: string, skipPost?: boolean) => Promise<void>;
  connectTikTok: (redirectTo?: string) => Promise<void>;
  connectTikTokBusiness: (redirectTo?: string) => Promise<void>;
  disconnectTikTok: (connectionId: string) => Promise<void>;
  loadAccounts: () => Promise<TikTokAccount[]>;
}

const SOCIAL_ACCOUNTS_ENDPOINT = "/api/social/accounts?types=tiktok,tiktok_business";
const TIKTOK_STATUS_ENDPOINT = "/api/tiktok/social-fetch-status";
const TIKTOK_DISCONNECT_ENDPOINT = "/api/tiktok/disconnect";

const ACTIVE_STATUSES = new Set(["in_progress", "processing", "running"]);

export function useTikTokConnections(): UseTikTokConnectionsReturn {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [accounts, setAccounts] = useState<TikTokAccount[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { isIndexing, currentMessageIndex, indexingMessages, triggerIndex } = useTikTokIndex();
  const previousAccountIds = useRef<Set<string>>(new Set());
  const oauthHandledRef = useRef<boolean>(false);

  const loadAccounts = useCallback(async (): Promise<TikTokAccount[]> => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(SOCIAL_ACCOUNTS_ENDPOINT, { cache: "no-store" });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to load TikTok accounts");
      const tiktok = (data?.accounts || []).filter(
        (a: { connectionType: string }) => a.connectionType === "tiktok" || a.connectionType === "tiktok_business"
      );
      setAccounts(tiktok);
      return tiktok;
    } catch (e: any) {
      setError(e?.message || "Failed to load TikTok accounts");
      setAccounts([]);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const searchParams = new URLSearchParams(window.location.search || "");
    const tiktokOauthStatus = searchParams.get("tiktok_oauth");
    const tiktokAccountId = searchParams.get("tiktok_account_id");

    const wasRedirectedFromTikTok =
      sessionStorage.getItem(sessionKeys.tiktokConnecting) === "true" ||
      tiktokOauthStatus === "success";

    if (wasRedirectedFromTikTok && !oauthHandledRef.current) {
      oauthHandledRef.current = true;
      try {
        sessionStorage.removeItem(sessionKeys.tiktokConnecting);
      } catch {}

      setTimeout(() => {
        loadAccounts().then((loadedAccounts) => {
          if (loadedAccounts && loadedAccounts.length > 0) {
            // If we have the specific account ID from callback, find and index that account
            // Otherwise fall back to the first account (backward compatibility)
            const account = tiktokAccountId
              ? loadedAccounts.find(acc => acc.id === tiktokAccountId)
              : loadedAccounts[0];

            if (account) {
              const sessionKey = sessionKeys.tiktokIndexed(account.id);
              let hasSessionFlag = false;
              try { hasSessionFlag = sessionStorage.getItem(sessionKey) === "true"; } catch {}

              console.log("[AUTO-INDEX:TT] triggering auto-index (post-OAuth)", {
                accountId: account.id,
                connectionId: account.connectionId,
                hasSessionFlag,
                isCurrentlyIndexing: !!isIndexing[account.id]
              });
              // Set session flag before triggering
              try { sessionStorage.setItem(sessionKey, "true"); } catch {}
              triggerIndex(account.id, account.connectionId).then(() => {
                console.log("[AUTO-INDEX:TT] triggerIndex promise resolved", { accountId: account.id });
              }).catch((err) => {
                console.error("[AUTO-INDEX:TT] triggerIndex promise rejected", { accountId: account.id, error: err });
              });
            } else if (tiktokAccountId) {
              console.warn("[AUTO-INDEX:TT] Could not find account with ID from callback:", tiktokAccountId);
            }
          }
        });
      }, 500);
    }
  }, [loadAccounts, triggerIndex]);

  const checkAutoIndexingStatus = useCallback(async () => {
    if (accounts.length === 0) return;

    const recentTime = new Date(Date.now() - 5 * 60 * 1000).toISOString();

    for (const account of accounts) {
      if (isIndexing[account.id]) continue;

      const sessionKey = sessionKeys.tiktokIndexed(account.id);
      if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(sessionKey) === "true") {
        console.log("[AUTO-INDEX:TT] already auto-indexed, skipping", { accountId: account.id });
        continue;
      }

      try {
        const qs = new URLSearchParams({
          connectionId: account.connectionId,
          since: recentTime,
        });
        const resp = await fetch(`${TIKTOK_STATUS_ENDPOINT}?${qs.toString()}`, { cache: "no-store" });
        const json = await resp.json();
        const status = (json?.status || "").toString().toLowerCase();

        console.log("[AUTO-INDEX:TT] status check", { accountId: account.id, status });

        if (ACTIVE_STATUSES.has(status)) {
          console.log("[AUTO-INDEX:TT] triggering auto-index (resume active)", { accountId: account.id, status });
          try { sessionStorage.setItem(sessionKey, "true"); } catch {}
          triggerIndex(account.id, account.connectionId);
        }
      } catch {
        // Ignore errors
      }
    }
  }, [accounts, isIndexing, triggerIndex]);

  useEffect(() => {
    if (accounts.length === 0) return;

    const pollInterval = setInterval(checkAutoIndexingStatus, 5000);
    return () => clearInterval(pollInterval);
  }, [accounts.length, checkAutoIndexingStatus]);

  useEffect(() => {
    if (accounts.length > 0) {
      const currentAccountIds = new Set(accounts.map((account) => account.id));
      const previousAccountIdsSet = previousAccountIds.current;

      if (previousAccountIdsSet.size > 0) {
        const newAccountIds = Array.from(currentAccountIds).filter((id) => !previousAccountIdsSet.has(id));
        if (newAccountIds.length > 0) {
          const newAccounts = accounts.filter((account) => newAccountIds.includes(account.id));
          newAccounts.forEach((account) => {
            // Check session storage before triggering
            const sessionKey = sessionKeys.tiktokIndexed(account.id);
            let alreadyIndexed = false;
            try { alreadyIndexed = sessionStorage.getItem(sessionKey) === "true"; } catch {}

            if (!alreadyIndexed) {
              console.log("[AUTO-INDEX:TT] new account detected, triggering index", { accountId: account.id });
              triggerIndex(account.id, account.connectionId);
            } else {
              console.log("[AUTO-INDEX:TT] new account detected but already indexed this session", { accountId: account.id });
            }
          });
        }
      } else {
        setTimeout(checkAutoIndexingStatus, 2000);
      }

      previousAccountIds.current = currentAccountIds;
    }
  }, [accounts, triggerIndex, checkAutoIndexingStatus]);

  const connectTikTok = useCallback(async (redirectTo?: string) => {
    try {
      setIsConnecting(true);
      sessionStorage.setItem(sessionKeys.tiktokConnecting, "true");
      await connectWithTikTok(redirectTo ?? "/account");
    } finally {
      setIsConnecting(false);
    }
  }, []);

  const connectTikTokBusiness = useCallback(async (redirectTo?: string) => {
    try {
      setIsConnecting(true);
      sessionStorage.setItem(sessionKeys.tiktokConnecting, "true");
      await connectWithTikTokBusiness(redirectTo ?? "/account");
    } finally {
      setIsConnecting(false);
    }
  }, []);

  const disconnectTikTok = useCallback(
    async (connectionId: string) => {
      try {
        const res = await fetch(TIKTOK_DISCONNECT_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ connectionId }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error((data as any)?.error || "Failed to disconnect TikTok account");
        }
        await loadAccounts();
      } catch (e: any) {
        setError(e?.message || "Failed to disconnect TikTok account");
      }
    },
    [loadAccounts]
  );

  return useMemo(
    () => ({
      isLoading,
      isConnecting,
      accounts,
      error,
      indexingMessages,
      isIndexing,
      currentMessageIndex,
      triggerIndex,
      connectTikTok,
      connectTikTokBusiness,
      disconnectTikTok,
      loadAccounts,
    }),
    [
      isLoading,
      isConnecting,
      accounts,
      error,
      indexingMessages,
      isIndexing,
      currentMessageIndex,
      triggerIndex,
      connectTikTok,
      connectTikTokBusiness,
      disconnectTikTok,
      loadAccounts,
    ]
  );
}


